/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { AiringOffer, ProgramType } from 'client-code/airing';
import { PreviewType } from 'client-code/previews';

import { LEFT_QUOTE, RIGHT_QUOTE } from '../../../config';
import { AppUtils } from '../../AppUtils';
import { ServicesClientImpl } from '../../interactors/ServicesClientImpl';
import { Locale } from '../../locale/Locale';
import { LocaleKeys } from '../../locale/LocaleKeys';
import { TOfferPreviewCustomData } from '../../types/TOfferPreviewCustomData';
import { DateTime } from '../../utils';

export class AiringPreviewUtils {
    private airingData: AiringOffer[] | undefined;

    private channelTitleText: string = '';

    private channelTitleTextNext: string = '';

    private checkAiringDataLength: number = 0;

    private parentalControlsRatingSeasonEpisodeSeperator: boolean = false;

    private parentalControlsRatingStarRatingSeperator: boolean = false;

    private previewData: TOfferPreviewCustomData | undefined;

    private timeParentalControlsRatingSeperator: boolean = false;

    public constructor(airingOffer?: AiringOffer[], offerPreview?: TOfferPreviewCustomData) {
        this.airingData = airingOffer;
        this.previewData = offerPreview;
        this.checkAiringDataLength = this.airingData?.length || 0;
        // TODO: Evaluate and implement if below three calls calls can be removed from constructor
        this._evaluateMetadataSeperators();
        this._formatChannelTitle();
        this._formatChannelTitleNext();
    }

    private _evaluateMetadataSeperators = () => {
        const isInternalRatingLoaded = this.isInternalRatingLoaded();
        const getTime = this.getTime();

        this.timeParentalControlsRatingSeperator = !!(getTime && isInternalRatingLoaded);
        this.parentalControlsRatingSeasonEpisodeSeperator = !!(
            (getTime || isInternalRatingLoaded) &&
            this.getSeasonEpisode() !== null
        );
        this.parentalControlsRatingStarRatingSeperator = !!(
            (getTime || isInternalRatingLoaded) &&
            this.getStarRating() !== 0
        );
    };

    private _formatChannelTitle = () => {
        if (
            (this.getProgramType() === ProgramType.Movie ||
                this.getProgramType() === PreviewType.MoviePreview) &&
            this.getMovieYear() &&
            !this.shouldHideMetadata()
        ) {
            this.channelTitleText = `${this.getChannelTitle()} (${this.getMovieYear()})`;
        } else {
            this.channelTitleText = this.getChannelTitle();
        }
    };

    private _formatChannelTitleNext = () => {
        if (
            this.getProgramTypeNext() === ProgramType.Movie &&
            !this.hasAdultNext() &&
            this.getMovieYearNext()
        ) {
            this.channelTitleTextNext = `${this.getChannelTitleNext()} (${this.getMovieYearNext()})`;
        } else {
            this.channelTitleTextNext = this.getChannelTitleNext();
        }
    };

    public generateCast = () => {
        const COMMA = ',';
        const castText = this.previewData?.contentPreview?.creditString;
        const castLength = 125;
        if (castText && castText.length > castLength) {
            const castString = castText.slice(0, castLength);

            let castList =
                castString[castLength - 1] === COMMA
                    ? castString.slice(0, castLength - 1)
                    : castString.split(COMMA);

            if (Array.isArray(castList)) {
                castList.pop();
                castList = castList.join(COMMA);
            }
            return castList;
        }
        return castText;
    };

    public generateGenre() {
        const COMMA = ',';
        const genreLength =
            this.getProgramType() === ProgramType.Movie || this.getFirstAireddate() === ''
                ? 27
                : 23;

        const genreText =
            this.airingData?.[0]?.logging?.genre ||
            this.previewData?.contentPreview?.categoryLabel ||
            '';

        if (genreText.length > genreLength) {
            // Slice and handle the case where the genre ends with a comma
            let genreString = genreText.slice(0, genreLength);
            if (genreString.endsWith(COMMA)) {
                genreString = genreString.slice(0, -1);
            }

            // Split and remove last element if it ends with a comma
            const genreList = genreString.split(COMMA);
            if (Array.isArray(genreList)) {
                genreList.pop();
                return genreList.join(COMMA);
            }
        }

        return genreText;
    }

    public getSeasonEpisodeTitle = () => {
        let title = '';
        const seasonEpisode = this.getSeasonEpisode();
        const episodeTitle = this.getEpisodeTitle();
        title += `${this.getChannelTitleText()}`;
        if (seasonEpisode) {
            title += ` - ${seasonEpisode}`;
        }
        if (!seasonEpisode && episodeTitle) {
            title += ` - ${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`;
        } else if (episodeTitle) {
            title += ` ${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`;
        }
        return title;
    };

    public getCast = () => {
        if (this.shouldHideMetadata()) return null;
        return this.generateCast();
    };

    public getChannelTitle = (): string => {
        if (this.shouldHideMetadata()) return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        if (this.airingData && this.airingData[0]?.banner?.title)
            return this.airingData[0]?.banner?.title;
        if (this.previewData?.contentPreview?.title) return this.previewData?.contentPreview?.title;
        return Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
    };

    public getChannelTitleNext = (): string => {
        if (this.shouldHideMetadata()) return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        if (this.airingData && this.airingData[1]?.banner?.title)
            return this.airingData[1]?.banner?.title;
        if (this.previewData?.contentPreview?.title) return this.previewData?.contentPreview?.title;
        return Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
    };

    public getChannelTitleText = (): string => {
        return this.channelTitleText || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
    };

    public getChannelTitleTextNext = (): string => {
        return this.channelTitleTextNext || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
    };

    public getDescription = () => {
        if (this.previewData?.contentPreview?.description) {
            return this.shouldHideMetadata()
                ? Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN)
                : this.previewData.contentPreview?.description;
        }
        return Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE);
    };

    public getDuration = (): number => {
        if (this.airingData && this.airingData[0]?.duration) return this.airingData[0].duration;
        if (this.previewData?.contentPreview?.duration)
            return this.previewData?.contentPreview?.duration;
        return 0;
    };

    public getEndTime = (): Date => {
        if (this.getDuration() !== 0 && this.getStartTime() !== '')
            return AppUtils.addSecondsToDateTime(new Date(this.getStartTime()), this.getDuration());
        if (this.previewData?.contentPreview?.endTime)
            return new Date(this.previewData?.contentPreview?.endTime);
        return new Date('');
    };

    public getEpisodeTitle = () => {
        const subtitle = this.airingData && this.airingData[0]?.banner?.subtitle;
        const episodeTitle = this.previewData?.contentPreview?.episodeTitle;
        if (this.shouldHideMetadata()) return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        if (subtitle) return subtitle;
        if (episodeTitle) return episodeTitle;
        return '';
    };

    public getFirstAireddate = (): string | null => {
        if (this.getProgramType() === ProgramType.Movie) return null;
        const originalAirDate = this.airingData && this.airingData[0]?.logging?.originalAirDate;
        const firstAiredDate = this.previewData?.contentPreview?.firstAiredDate;
        const sAiringDate = originalAirDate || firstAiredDate;
        if (!sAiringDate) return null;
        const airingEpoch = new Date(sAiringDate).getTime();
        const nowEpoch = new Date().getTime();
        const prefixText =
            airingEpoch >= nowEpoch
                ? Locale.getString(LocaleKeys.FIRST_AIRS)
                : Locale.getString(LocaleKeys.FIRST_AIRED);
        return AppUtils.strftime(sAiringDate, `${prefixText} %M/%d/%y`);
    };

    public getGenre = () => {
        if (!this.airingData || this.checkAiringDataLength === 0) {
            return false;
        }
        if (this.hasGenre() && this.airingData[0].logging.genre !== undefined) {
            return this.generateGenre();
        }
        return false;
    };

    public getIsAvailableInSocu = (): boolean => {
        if (!this.previewData) {
            return false;
        }
        return !!this.previewData?.isAvailableInSocu;
    };

    public getMovieYear = (): number => {
        return (
            this.airingData?.[0]?.banner?.movieYear ||
            this.previewData?.contentPreview?.movieYear ||
            0
        );
    };

    public getMovieYearNext = (): number => {
        if (this.airingData && this.airingData[1]?.banner?.movieYear)
            return this.airingData[1]?.banner?.movieYear;
        return 0;
    };

    public getParentalControlsRating = (): any => {
        if (this.airingData && this.airingData[0]?.parentalControls?.rating)
            return this.airingData[0]?.parentalControls?.rating;
        if (this.previewData?.contentPreview?.internalRatingList)
            return this.previewData?.contentPreview?.internalRatingList;
        return null;
    };

    public getParentalControlsRatingSeasonEpisodeSeperator() {
        return this.parentalControlsRatingSeasonEpisodeSeperator;
    }

    public getParentalControlsRatingStarRatingSeperator() {
        return this.parentalControlsRatingStarRatingSeperator;
    }

    public getProgramType = (): string => {
        return (
            (this.airingData && this.airingData[0]?.banner?.programType) ||
            (this.previewData && this.previewData?.contentPreview?.previewType) ||
            ''
        );
    };

    public getProgramTypeNext = (): string => {
        return (this.airingData && this.airingData[1]?.banner?.programType) || '';
    };

    public getSeasonEpisode = (): any => {
        if (
            this.airingData &&
            this.airingData[0]?.banner?.seasonNumber &&
            this.airingData[0]?.banner?.episodeNumber
        ) {
            return AppUtils.generateSeasonEpisodeText(
                this.airingData[0].banner.seasonNumber,
                this.airingData[0].banner.episodeNumber,
            );
        }

        const contentPreview = this.previewData?.contentPreview;
        if (contentPreview?.seasonNumber && contentPreview?.episodeNumber) {
            return AppUtils.generateSeasonEpisodeText(
                contentPreview.seasonNumber,
                contentPreview.episodeNumber,
            );
        }
        return null;
    };

    public getShowAttributes = (currentPlaybackResolution?: number) => {
        const showAttributes: string[] = [];
        if (this.getVideoResolution().length) {
            showAttributes.push(this.getVideoResolution().toLocaleUpperCase());
        }

        if (this.hasCC()) {
            showAttributes.push('CC');
        }

        if (this.hasSAP()) {
            showAttributes.push('SAP');
        }

        if (currentPlaybackResolution) {
            showAttributes.push(`${currentPlaybackResolution}p`);
        }

        if (showAttributes.length === 0) {
            return false;
        }

        const showAttributesString = showAttributes.join(', ');

        return showAttributesString;
    };

    public getStarRating = (): number => {
        return (
            this.airingData?.[0]?.banner?.starRating ||
            this.previewData?.contentPreview?.starRating ||
            0
        );
    };

    public getStartTime = (): string => {
        if (this.airingData && this.airingData[0]?.startTime) return this.airingData[0].startTime;
        if (this.previewData?.contentPreview?.startTime)
            return this.previewData?.contentPreview?.startTime;
        return '';
    };

    public getTime(): any {
        if (this.getStartTime() !== '' && this.getEndTime()) {
            return AppUtils.generateTimeText(new Date(this.getStartTime()), this.getEndTime());
        }
        return null;
    }

    public getFormattedStartTime(): any {
        if (this.getStartTime() !== '') {
            const oStartTime = new DateTime(this.getStartTime());
            return `${oStartTime.strftime('%a %m/%D')} ${oStartTime.to12HourClock()}`;
        }
        return null;
    }

    public getTimeParentalControlsRatingSeperator() {
        return this.timeParentalControlsRatingSeperator;
    }

    public getVideoResolution = () => {
        let videoResolution = '';
        if (this.airingData && this.airingData[0]?.logging?.videoResolution) {
            videoResolution = this.airingData[0]?.logging?.videoResolution;
        } else if (this.previewData?.videoResolution) {
            videoResolution = this.previewData.videoResolution;
        } else return '';
        return videoResolution;
    };

    public shouldHideMetadata = (): boolean => {
        // Note: `airingData` is not present for SoCu content.
        if (
            (this.airingData &&
                this.checkAiringDataLength &&
                this.airingData[0].parentalControls.isAdult) ||
            this.previewData?.contentPreview?.isAdult
        ) {
            const services = new ServicesClientImpl();
            return services.shouldHideMetadata();
        }
        return false;
    };

    public hasAdultNext = (): boolean => {
        if (!this.airingData) {
            return false;
        }
        if (this.checkAiringDataLength && this.airingData[1].parentalControls.isAdult) return true;
        return false;
    };

    public hasCC = (): boolean => {
        return !!(this.previewData && this.previewData.hasCc);
    };

    public hasGenre = () => {
        if (!this.airingData) {
            return false;
        }
        return !!(
            this.checkAiringDataLength &&
            this.airingData[0].logging.genre &&
            this.airingData[0].logging.genre !== '' &&
            this.airingData[0].logging.genre !== undefined
        );
    };

    public hasNew = (): boolean => {
        const isNewFromAiring = this.airingData && this.airingData[0]?.banner?.isNew;
        const isNewFromPreview = this.previewData?.contentPreview?.isNew;
        return !!isNewFromAiring || !!isNewFromPreview;
    };

    public hasNewNext = (): boolean => {
        if (!this.airingData || this.checkAiringDataLength === 0) {
            return false;
        }
        return !!(this.airingData && this.airingData[1]?.banner?.isNew);
    };

    public hasSAP = (): boolean => {
        return !!(this.previewData && this.previewData.hasSap);
    };

    public isInternalRatingLoaded = (): boolean => {
        const services = new ServicesClientImpl();
        let flag = false;
        const parentalControlsRating = this.getParentalControlsRating();
        if (parentalControlsRating) {
            parentalControlsRating.forEach(({ typeId, valueId }: any) => {
                const servieCall = services.getInternalRatingName(typeId + valueId);
                if (servieCall) {
                    flag = true;
                }
            });
        }
        return flag;
    };
}
